/**
 * PartInfoModal Component
 * Full details modal shown on click of the ℹ️ icon in Parts List.
 * Displays all 4 phases of AI classification with complete reasoning.
 */
import React from 'react';
import { 
  X, Search, Sparkles, Scale, FileText, Filter, Check, 
  ChevronRight, RefreshCw, AlertCircle, ArrowRight 
} from 'lucide-react';

const phaseConfig = {
  phase1: {
    title: 'Understanding the Part',
    subtitle: 'Extracting functional essence',
    icon: Search,
    iconBg: 'from-blue-600 to-blue-700'
  },
  phase2_5: {
    title: 'Pre-Filter Candidates',
    subtitle: 'Finding candidate subgroups',
    icon: Filter,
    iconBg: 'from-purple-600 to-purple-700'
  },
  phase2: {
    title: 'Semantic Search',
    subtitle: 'Evaluating candidates for match',
    icon: Sparkles,
    iconBg: 'from-indigo-600 to-indigo-700'
  },
  phase3: {
    title: 'Identifying Product Family',
    subtitle: 'Finding the right shelf label',
    icon: Scale,
    iconBg: 'from-amber-600 to-amber-700'
  },
  phase4: {
    title: 'Naming & Metadata',
    subtitle: 'Creating sub-group profile',
    icon: FileText,
    iconBg: 'from-emerald-600 to-emerald-700'
  }
};

// Phase 1 Content
const Phase1Content = ({ output }) => {
  if (!output) return null;
  
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <p className="text-slate-500 text-xs">Primary Function</p>
          <p className="text-white text-sm">{output.primaryFunction}</p>
        </div>
        <div>
          <p className="text-slate-500 text-xs">Category</p>
          <p className="text-white text-sm">{output.category}</p>
        </div>
        <div>
          <p className="text-slate-500 text-xs">Type</p>
          <p className="text-white text-sm">{output.type}</p>
        </div>
      </div>
      {output.functionalEssence && (
        <div className="mt-3 p-3 bg-blue-900/20 border border-blue-500/30 rounded-lg">
          <p className="text-blue-400 text-xs font-medium">Functional Essence:</p>
          <p className="text-blue-300 text-sm">{output.functionalEssence}</p>
        </div>
      )}
      {output.searchTerms?.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {output.searchTerms.slice(0, 6).map((term, i) => (
            <span key={i} className="px-2 py-0.5 bg-slate-800 rounded text-xs text-slate-400">
              {term}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

// Phase 2.5 Content
const Phase25Content = ({ output }) => {
  if (!output) return null;
  
  return (
    <div className="p-3 bg-purple-900/20 border border-purple-500/30 rounded-lg">
      <p className="text-purple-300 text-sm">
        Found <span className="font-medium">{output.candidatesFound || 0}</span> candidates 
        from <span className="font-medium">{output.subgroupsEvaluated || 0}</span> subgroups
      </p>
    </div>
  );
};

// Phase 2 Content
const Phase2Content = ({ output }) => {
  if (!output) return null;
  
  const matchFound = output.matchFound;
  
  return (
    <div>
      {output.evaluationLog?.length > 0 && (
        <div className="mb-3 space-y-1 max-h-24 overflow-y-auto">
          {output.evaluationLog.slice(0, 4).map((entry, i) => (
            <div key={i} className="flex items-center gap-2 text-sm">
              {entry.isMatch ? (
                <Check className="w-3 h-3 text-emerald-400" />
              ) : (
                <X className="w-3 h-3 text-slate-500" />
              )}
              <span className={entry.isMatch ? 'text-emerald-300' : 'text-slate-400'}>{entry.subGroupName}</span>
            </div>
          ))}
        </div>
      )}
      <div className={`p-3 rounded-lg border ${matchFound ? 'bg-emerald-900/20 border-emerald-500/30' : 'bg-amber-900/20 border-amber-500/30'}`}>
        <p className={`text-sm ${matchFound ? 'text-emerald-300' : 'text-amber-300'}`}>
          {output.matchReasoning || (matchFound ? 'Match found!' : 'No existing match')}
        </p>
      </div>
    </div>
  );
};

// Phase 3 Content (New Shelf Label structure)
const Phase3Content = ({ output }) => {
  if (!output) return null;
  
  const { analysis, shelfLabelOptions, recommendation } = output;
  
  // Legacy structure fallback
  if (output.evaluations && !shelfLabelOptions) {
    return (
      <div className="space-y-2">
        {output.evaluations?.slice(0, 3).map((ev, i) => (
          <div key={i} className="p-2 bg-slate-800/50 rounded-lg">
            <span className="text-white text-sm">{ev.level}</span>
            <span className="text-slate-500 text-xs ml-2">({ev.verdict})</span>
          </div>
        ))}
        <div className="p-3 bg-emerald-900/20 border border-emerald-500/30 rounded-lg">
          <p className="text-emerald-300 text-sm">Optimal: {output.optimalLevel}</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-3">
      {/* What it does */}
      {analysis?.whatDoesItDo && (
        <div className="p-3 bg-blue-900/20 border border-blue-500/30 rounded-lg">
          <p className="text-blue-400 text-xs font-medium">What it does:</p>
          <p className="text-white text-sm">{analysis.whatDoesItDo}</p>
          {analysis.primaryFunction && (
            <p className="text-slate-400 text-xs mt-2">
              Primary: {analysis.primaryFunction}
            </p>
          )}
        </div>
      )}
      
      {/* Shelf Label Options */}
      {shelfLabelOptions?.length > 0 && (
        <div className="space-y-2">
          {shelfLabelOptions.map((option, i) => {
            const isOptimal = recommendation && option.label === recommendation.shelfLabel;
            return (
              <div 
                key={i} 
                className={`p-2 rounded-lg border ${
                  isOptimal 
                    ? 'bg-emerald-900/30 border-emerald-500/50' 
                    : 'bg-slate-800/50 border-slate-700/50'
                }`}
              >
                <div className="flex items-center gap-2">
                  <span className={isOptimal ? 'text-emerald-400' : 'text-slate-500'}>
                    {isOptimal ? '✓' : option.wouldHumanUseThis ? '~' : '✗'}
                  </span>
                  <span className={`text-sm ${isOptimal ? 'text-emerald-300 font-medium' : 'text-slate-300'}`}>
                    {option.label}
                  </span>
                  {isOptimal && (
                    <span className="px-1.5 py-0.5 bg-emerald-500/20 text-emerald-400 text-xs rounded">
                      RECOMMENDED
                    </span>
                  )}
                </div>
                <p className="text-slate-500 text-xs mt-1 ml-5">{option.reasoning}</p>
              </div>
            );
          })}
        </div>
      )}
      
      {/* What belongs / doesn't belong */}
      {recommendation && (
        <div className="mt-3 space-y-2">
          {recommendation.whatBelongsHere?.length > 0 && (
            <div>
              <p className="text-emerald-400/70 text-xs mb-1">This sub-group includes:</p>
              <div className="flex flex-wrap gap-1">
                {recommendation.whatBelongsHere.slice(0, 4).map((item, i) => (
                  <span key={i} className="px-2 py-0.5 bg-emerald-900/30 rounded text-xs text-emerald-300">
                    ✓ {item}
                  </span>
                ))}
              </div>
            </div>
          )}
          {recommendation.whatDoesNotBelong?.length > 0 && (
            <div>
              <p className="text-red-400/70 text-xs mb-1">Does not include:</p>
              <div className="flex flex-wrap gap-1">
                {recommendation.whatDoesNotBelong.slice(0, 4).map((item, i) => (
                  <span key={i} className="px-2 py-0.5 bg-red-900/20 rounded text-xs text-red-300">
                    ✗ {item}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Phase 4 Content
const Phase4Content = ({ output }) => {
  if (!output) return null;
  
  return (
    <div className="space-y-3">
      {/* Sub-group name */}
      <div className="p-3 bg-emerald-900/20 border border-emerald-500/30 rounded-lg">
        <p className="text-emerald-400 text-xs font-medium">Sub-Group Name:</p>
        <p className="text-white text-lg font-semibold">{output.name}</p>
        {output.description && (
          <p className="text-slate-400 text-sm mt-1">{output.description}</p>
        )}
      </div>
      
      {/* Main Group Recommendation */}
      {output.mainGroupRecommendation && (
        <div className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-lg">
          <div>
            <p className="text-slate-500 text-xs">Main Group</p>
            <p className="text-white text-sm">{output.mainGroupRecommendation.id} - {output.mainGroupRecommendation.name}</p>
          </div>
          <ChevronRight className="w-4 h-4 text-slate-600" />
          <div>
            <p className="text-blue-400 text-xs">Sub-Group</p>
            <p className="text-white text-sm">{output.name}</p>
          </div>
        </div>
      )}
      
      {/* Accommodates */}
      {output.accommodates?.length > 0 && (
        <div>
          <p className="text-slate-500 text-xs mb-1">Accommodates:</p>
          <div className="flex flex-wrap gap-1">
            {output.accommodates.slice(0, 5).map((item, i) => (
              <span key={i} className="px-2 py-0.5 bg-emerald-900/20 rounded text-xs text-emerald-300">
                {item}
              </span>
            ))}
          </div>
        </div>
      )}
      
      {/* Synonyms */}
      {output.synonyms?.length > 0 && (
        <div>
          <p className="text-slate-500 text-xs mb-1">Synonyms:</p>
          <div className="flex flex-wrap gap-1">
            {output.synonyms.slice(0, 4).map((syn, i) => (
              <span key={i} className="px-2 py-0.5 bg-slate-800 rounded text-xs text-slate-400">
                {syn}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Phase Section Component
const PhaseSection = ({ phaseKey, phaseData, config }) => {
  const Icon = config.icon;
  const output = phaseData?.output;
  
  const getContent = () => {
    switch (phaseKey) {
      case 'phase1': return <Phase1Content output={output} />;
      case 'phase2_5': return <Phase25Content output={output} />;
      case 'phase2': return <Phase2Content output={output} />;
      case 'phase3': return <Phase3Content output={output} />;
      case 'phase4': return <Phase4Content output={output} />;
      default: return null;
    }
  };
  
  if (!phaseData) return null;
  
  return (
    <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-3 p-4 border-b border-slate-700/30">
        <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${config.iconBg} flex items-center justify-center`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1">
          <h4 className="text-white font-medium">{config.title}</h4>
          <p className="text-slate-500 text-xs">{config.subtitle}</p>
        </div>
        {phaseData.durationMs && (
          <span className="text-slate-500 text-xs">{(phaseData.durationMs / 1000).toFixed(1)}s</span>
        )}
      </div>
      
      {/* Content */}
      <div className="p-4">
        {getContent()}
      </div>
    </div>
  );
};

// Main Modal Component
const PartInfoModal = ({ 
  isOpen, 
  onClose, 
  part, 
  classificationData,
  onReclassify,
  isReclassifying = false
}) => {
  if (!isOpen) return null;
  
  const hasClassification = classificationData && classificationData.phases;
  const phases = classificationData?.phases || {};
  const finalResult = classificationData?.finalResult;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 rounded-2xl shadow-2xl w-full max-w-3xl mx-4 max-h-[85vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-slate-700/50">
          <div className="flex items-center gap-3">
            <Sparkles className="w-6 h-6 text-blue-400" />
            <div>
              <h2 className="text-lg font-semibold text-white">AI Classification Details</h2>
              <p className="text-slate-400 text-sm">{part?.hpn_display}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>
        
        {/* Content */}
        <div className="p-5 overflow-y-auto max-h-[calc(85vh-140px)]">
          {/* Part Summary */}
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4 mb-5">
            <p className="text-slate-500 text-xs uppercase tracking-wider mb-1">Part</p>
            <h3 className="text-white font-medium mb-2">{part?.description}</h3>
            <div className="flex gap-4 text-sm">
              <span className="text-slate-400">
                <span className="text-slate-500">Manufacturer:</span> {part?.manufacturer_name}
              </span>
              <span className="text-slate-400">
                <span className="text-slate-500">MPN:</span> <span className="font-mono">{part?.mpn}</span>
              </span>
            </div>
          </div>
          
          {!hasClassification ? (
            // No classification data
            <div className="text-center py-10">
              <AlertCircle className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400 font-medium mb-2">Classification not available</p>
              <p className="text-slate-500 text-sm mb-6">
                This part was created before AI classification was implemented.
              </p>
              {onReclassify && (
                <button
                  onClick={onReclassify}
                  disabled={isReclassifying}
                  className="px-6 py-3 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white rounded-xl flex items-center gap-2 mx-auto transition-colors"
                >
                  {isReclassifying ? (
                    <>
                      <RefreshCw className="w-5 h-5 animate-spin" />
                      Running AI Classification...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5" />
                      Run AI Classification
                    </>
                  )}
                </button>
              )}
            </div>
          ) : (
            // Show phases
            <div className="space-y-4">
              {/* Phase 1 */}
              {phases.phase1 && (
                <PhaseSection 
                  phaseKey="phase1" 
                  phaseData={phases.phase1} 
                  config={phaseConfig.phase1} 
                />
              )}
              
              {/* Phase 2.5 */}
              {phases.phase2_5 && (
                <PhaseSection 
                  phaseKey="phase2_5" 
                  phaseData={phases.phase2_5} 
                  config={phaseConfig.phase2_5} 
                />
              )}
              
              {/* Phase 2 */}
              {phases.phase2 && (
                <PhaseSection 
                  phaseKey="phase2" 
                  phaseData={phases.phase2} 
                  config={phaseConfig.phase2} 
                />
              )}
              
              {/* Phase 3 */}
              {phases.phase3 && (
                <PhaseSection 
                  phaseKey="phase3" 
                  phaseData={phases.phase3} 
                  config={phaseConfig.phase3} 
                />
              )}
              
              {/* Phase 4 */}
              {phases.phase4 && (
                <PhaseSection 
                  phaseKey="phase4" 
                  phaseData={phases.phase4} 
                  config={phaseConfig.phase4} 
                />
              )}
              
              {/* Final Result */}
              {finalResult && (
                <div className="mt-5 p-4 bg-gradient-to-r from-blue-900/30 to-emerald-900/30 border border-blue-500/30 rounded-xl">
                  <p className="text-slate-400 text-xs uppercase tracking-wider mb-2">Final Classification</p>
                  <div className="flex items-center gap-3">
                    <div className="px-4 py-2 bg-slate-800 rounded-lg border border-slate-600">
                      <p className="text-slate-500 text-xs">Main Group</p>
                      <p className="text-white font-medium">{finalResult.mainGroupCode} - {finalResult.mainGroupName}</p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-slate-600" />
                    <div className="px-4 py-2 bg-blue-900/30 rounded-lg border border-blue-500/50">
                      <p className="text-blue-400 text-xs">Sub-Group {finalResult.isNewSubGroup ? '(New)' : ''}</p>
                      <p className="text-white font-medium">{finalResult.subGroupName}</p>
                    </div>
                  </div>
                  {classificationData.totalDurationMs && (
                    <p className="text-slate-500 text-xs mt-3">
                      {classificationData.totalApiCalls || 0} AI calls • {(classificationData.totalDurationMs / 1000).toFixed(1)}s total
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="p-4 border-t border-slate-700/50 flex justify-end">
          <button
            onClick={onClose}
            className="px-5 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default PartInfoModal;
